import { useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import {
  useDailyReport,
  useDailyReports,
  useFieldGrants,
  useSaveDailyReport,
  type DailyReport,
  type WorkLine,
} from '@/lib/field';
import { shortDate, today } from '@/lib/format';
import { useSite } from '@/lib/useSite';

/** 웹 공사일보 탭의 키 — 새로 쓰기는 등록, 고치기는 수정 권한이다(`ConstructionDailyReportController`). */
const DAILY_MENU = 'site_pmis_daily';

const SIGN_LABEL: Record<string, string> = {
  DRAFT: '작성중',
  IN_REVIEW: '결재중',
  COMPLETED: '완료',
  REJECTED: '반려',
};

/** 30일 전 — 목록은 최근 한 달을 본다. */
function monthAgo(): string {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
}

/**
 * 공사일보(내부 · V347) — 패드에서는 <b>글 칸</b>만 적는다.
 *
 * <p>웹 편집기는 A4 한 장(860px)을 그대로 그리는 종이 화면이고, 인력·장비·자재 표와 결재란·사진이 한 판에
 * 있다. 패드 폭에서 그 표를 옮기면 칸이 접혀 줄이 엉킨다. 그래서 여기서는 날씨·기온과 공정별 금일·명일
 * 작업내용·특기사항만 받고, 표는 저장된 값 그대로 서버가 다시 싣는다(웹의 전체 저장과 같은 문).
 *
 * <p>결재에 올린 일보는 담당만 고친다 — 막히면 서버가 답한다.
 */
export function ConstructionDailyPage() {
  const { siteId, siteName, readOnly } = useSite();
  const { can } = useFieldGrants();
  const canCreate = can(DAILY_MENU, 'create') && !readOnly;
  const canEdit = can(DAILY_MENU, 'edit') && !readOnly;

  const from = useMemo(monthAgo, []);
  const to = useMemo(today, []);
  const reports = useDailyReports(siteId, from, to);
  const rows = [...(reports.data ?? [])].sort((a, b) => b.reportDate.localeCompare(a.reportDate));
  const todayRow = rows.find((row) => row.reportDate === to);

  /** 열린 일보. `id` 가 null 이면 새로 쓰는 것이다. */
  const [opened, setOpened] = useState<{ id: string | null } | null>(null);

  return (
    <>
      <PageHeader
        title="공사일보"
        subtitle={siteName}
        action={
          canCreate && (
            // 오늘 것이 이미 있으면 그 장을 연다 — 같은 날 두 장은 서버가 받지 않는다.
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpened({ id: todayRow?.id ?? null })}>
              오늘 일보
            </button>
          )
        }
      />

      <QueryState pending={reports.isPending} error={reports.isError} empty={rows.length === 0} emptyText="최근 한 달의 공사일보가 없습니다.">
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" className="card block w-full text-left" onClick={() => setOpened({ id: row.id })}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-ui-md font-medium">{shortDate(row.reportDate)}</div>
                    <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {[row.weather, row.temperature].filter(Boolean).join(' · ')}
                      {row.todayWork ? ` · ${row.todayWork.split('\n')[0]}` : ''}
                    </div>
                  </div>
                  <span className="shrink-0 text-ui-2xs" style={{ color: 'var(--text-secondary)' }}>
                    {SIGN_LABEL[row.signStatus ?? 'DRAFT'] ?? row.signStatus}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </QueryState>

      {opened !== null && (
        <ReportModal
          siteId={siteId}
          id={opened.id}
          canWrite={opened.id === null ? canCreate : canEdit}
          onClose={() => setOpened(null)}
          onSaved={(saved) => setOpened({ id: saved.id })}
        />
      )}
    </>
  );
}

type Form = {
  reportDate: string;
  weather: string;
  temperature: string;
  todayWork: string;
  tomorrowPlan: string;
  specialNote: string;
  works: WorkLine[];
};

/**
 * 공정별 작업내용 줄 — 웹 `WorkWidget` 과 같은 규칙: 인력 줄의 공정 이름마다 한 줄, 그 밖에 내용이 적힌
 * 줄을 덧붙인다. 줄이 하나도 없으면 옛 두 칸(금일·명일 작업)을 쓴다.
 */
function workRowsOf(report: DailyReport | null): WorkLine[] {
  if (report === null) return [];
  const works = report.works ?? [];
  const rows: WorkLine[] = [];
  for (const line of report.labors ?? []) {
    const name = line.name.trim();
    if (name === '' || rows.some((row) => row.name === name)) continue;
    rows.push(works.find((work) => work.name === name) ?? { name, wbsItemId: line.wbsItemId ?? null, today: null, tomorrow: null });
  }
  for (const work of works) {
    if (!rows.some((row) => row.name === work.name) && (work.today || work.tomorrow)) rows.push(work);
  }
  return rows;
}

function formOf(report: DailyReport | null): Form {
  return {
    reportDate: report?.reportDate ?? today(),
    weather: report?.weather ?? '',
    temperature: report?.temperature ?? '',
    todayWork: report?.todayWork ?? '',
    tomorrowPlan: report?.tomorrowPlan ?? '',
    specialNote: report?.specialNote ?? '',
    works: workRowsOf(report),
  };
}

function ReportModal({
  siteId,
  id,
  canWrite,
  onClose,
  onSaved,
}: {
  siteId: string;
  id: string | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: (saved: DailyReport) => void;
}) {
  const detail = useDailyReport(siteId, id);
  const save = useSaveDailyReport(siteId);
  const loaded = id === null ? null : (detail.data ?? null);
  const [form, setForm] = useState<Form | null>(id === null ? formOf(null) : null);
  // 받은 일보로 칸을 한 번 채운다. 저장 뒤 새 응답이 와도 적고 있던 칸을 덮지 않는다.
  const current = form ?? (loaded !== null ? formOf(loaded) : null);

  const set = (patch: Partial<Form>) => setForm({ ...(current ?? formOf(null)), ...patch });

  const submit = () => {
    if (current === null) return;
    save.mutate(
      {
        id,
        body: {
          reportDate: current.reportDate,
          weather: current.weather.trim() || null,
          temperature: current.temperature.trim() || null,
          todayWork: current.todayWork.trim() || null,
          tomorrowPlan: current.tomorrowPlan.trim() || null,
          specialNote: current.specialNote.trim() || null,
          works: current.works.length === 0 ? null : current.works,
        },
      },
      {
        onSuccess: (saved) => {
          setForm(formOf(saved));
          onSaved(saved);
        },
      },
    );
  };

  const label = (text: string) => (
    <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>{text}</span>
  );

  return (
    <Modal
      title={current !== null ? `공사일보 ${shortDate(current.reportDate)}` : '공사일보'}
      maxWidth="44rem"
      onClose={onClose}
      actions={
        <>
          {canWrite && (
            <button
              type="button"
              className="btn-primary px-3 py-1.5 text-ui-xs"
              disabled={save.isPending || current === null}
              onClick={submit}
            >
              {save.isPending ? '저장 중…' : '저장'}
            </button>
          )}
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs" onClick={onClose}>
            닫기
          </button>
        </>
      }
    >
      <QueryState pending={id !== null && detail.isPending} error={id !== null && detail.isError} empty={false} emptyText="">
        {current !== null && (
          <fieldset disabled={!canWrite} className="flex flex-col gap-3">
            <div className="flex gap-2">
              <label className="flex-1">
                {label('일자')}
                <input type="date" className="input-field" value={current.reportDate} onChange={(e) => set({ reportDate: e.target.value })} />
              </label>
              <label className="flex-1">
                {label('날씨')}
                <input type="text" className="input-field" value={current.weather} onChange={(e) => set({ weather: e.target.value })} />
              </label>
              <label className="flex-1">
                {label('기온')}
                <input type="text" className="input-field" value={current.temperature} onChange={(e) => set({ temperature: e.target.value })} />
              </label>
            </div>

            {current.works.length > 0 ? (
              current.works.map((work, index) => (
                <div key={`${work.name}-${index}`} className="rounded-md border p-3" style={{ borderColor: 'var(--border)' }}>
                  <div className="mb-2 text-ui-sm font-medium">{work.name}</div>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <label className="flex-1">
                      {label('금일')}
                      <textarea
                        className="input-field"
                        rows={3}
                        value={work.today ?? ''}
                        onChange={(e) => set({ works: current.works.map((one, at) => (at === index ? { ...one, today: e.target.value } : one)) })}
                      />
                    </label>
                    <label className="flex-1">
                      {label('명일')}
                      <textarea
                        className="input-field"
                        rows={3}
                        value={work.tomorrow ?? ''}
                        onChange={(e) => set({ works: current.works.map((one, at) => (at === index ? { ...one, tomorrow: e.target.value } : one)) })}
                      />
                    </label>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col gap-2 md:flex-row">
                <label className="flex-1">
                  {label('금일 작업')}
                  <textarea className="input-field" rows={6} value={current.todayWork} onChange={(e) => set({ todayWork: e.target.value })} />
                </label>
                <label className="flex-1">
                  {label('명일 작업')}
                  <textarea className="input-field" rows={6} value={current.tomorrowPlan} onChange={(e) => set({ tomorrowPlan: e.target.value })} />
                </label>
              </div>
            )}

            <label>
              {label('특기사항')}
              <textarea className="input-field" rows={3} value={current.specialNote} onChange={(e) => set({ specialNote: e.target.value })} />
            </label>

            {save.isError && (
              <p className="text-ui-xs" style={{ color: 'var(--danger)' }}>저장하지 못했습니다.</p>
            )}
          </fieldset>
        )}
      </QueryState>
    </Modal>
  );
}
